import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Plus, Download, Ship, Archive, Eye, Pencil } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthStore } from '@/store/authStore';
import {
  getManifestsForProject,
  getVisibleProjects,
  archiveManifest,
} from '@/services/db/pob';
import type { POBManifest, POBManifestStatus } from '@/types/pob';

const STATUS_BADGE: Record<POBManifestStatus, string> = {
  draft: 'bg-amber-100 text-amber-800',
  published: 'bg-mozaic-teal/15 text-mozaic-teal',
  archived: 'bg-muted text-muted-foreground',
};

const WRITE_ROLES = ['ssr', 'supervisor', 'tenant_admin', 'platform_admin'];

export default function POBHomePage() {
  const navigate = useNavigate();
  const userRole = useAuthStore((s) => s.userRole);
  const projectIdClaim = useAuthStore((s) => s.projectId);
  const canWrite = !!userRole && WRITE_ROLES.includes(userRole);

  const projectsQ = useQuery({
    queryKey: ['pob_projects'],
    queryFn: getVisibleProjects,
  });

  const [selectedProject, setSelectedProject] = useState<string | null>(
    projectIdClaim ?? null
  );

  const activeProjectId = useMemo(() => {
    if (selectedProject) return selectedProject;
    if (projectIdClaim) return projectIdClaim;
    return projectsQ.data?.[0]?.id ?? null;
  }, [selectedProject, projectIdClaim, projectsQ.data]);

  const project = useMemo(
    () => projectsQ.data?.find((p) => p.id === activeProjectId) ?? null,
    [projectsQ.data, activeProjectId]
  );

  // Deterministic trigger label — never relies on Radix SelectValue's
  // implicit label extraction (which can briefly show the raw value
  // before items mount on first paint, surfacing a UUID — the S56 fix
  // attempted to mitigate this via SelectValue children but Radix only
  // honors the children path once items have registered their labels).
  const triggerLabel = project?.name
    ?? (projectsQ.isLoading ? 'Loading projects…' : 'Select project');

  const manifestsQ = useQuery({
    queryKey: ['pob_manifests', activeProjectId],
    queryFn: () => getManifestsForProject(activeProjectId!),
    enabled: !!activeProjectId,
  });

  const handleArchive = async (manifestId: string) => {
    if (!confirm('Archive this manifest?')) return;
    await archiveManifest(manifestId);
    manifestsQ.refetch();
  };

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Ship className="h-6 w-6 text-mozaic-teal" />
            Personnel on Board
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {project?.name ?? 'Select a project'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/templates/mozaic-pob-template.csv"
            download
            className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent"
          >
            <Download className="h-4 w-4" />
            Download Template
          </a>
          {canWrite && (
            <Button
              onClick={() => navigate('/pob/new')}
              className="bg-mozaic-teal hover:bg-mozaic-teal/90"
              disabled={!activeProjectId}
            >
              <Plus className="mr-1 h-4 w-4" />
              New Manifest
            </Button>
          )}
        </div>
      </header>

      {!projectIdClaim && projectsQ.data && projectsQ.data.length > 1 && (
        <div className="mb-4 flex max-w-md items-center gap-3">
          <Select
            value={activeProjectId ?? undefined}
            onValueChange={(v) => setSelectedProject(v)}
          >
            <SelectTrigger>
              <span className="truncate text-left">{triggerLabel}</span>
            </SelectTrigger>
            <SelectContent>
              {projectsQ.data.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {projectsQ.data.length} projects in scope
          </span>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {manifestsQ.isLoading ? (
            <div className="space-y-2 p-4">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : !manifestsQ.data || manifestsQ.data.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
              <Ship className="h-12 w-12 text-muted-foreground/60" />
              <p className="text-sm text-muted-foreground">
                No POB manifests yet for this project. Start by creating
                today's manifest.
              </p>
              {canWrite && (
                <Button
                  onClick={() => navigate('/pob/new')}
                  className="bg-mozaic-teal hover:bg-mozaic-teal/90"
                  disabled={!activeProjectId}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Create Today's Manifest
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Facility</TableHead>
                  <TableHead>Total POB</TableHead>
                  <TableHead>SSE Count</TableHead>
                  <TableHead>SSE %</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {manifestsQ.data.map((m) => (
                  <ManifestRow
                    key={m.id}
                    manifest={m}
                    canWrite={canWrite}
                    onArchive={handleArchive}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ManifestRow({
  manifest,
  canWrite,
  onArchive,
}: {
  manifest: POBManifest;
  canWrite: boolean;
  onArchive: (id: string) => void;
}) {
  const sseQ = useQuery({
    queryKey: ['pob_manifest_sse_count', manifest.id],
    queryFn: async () => {
      const { supabase } = await import('@/services/db/supabase');
      const { count: total } = await supabase
        .from('pob_entries')
        .select('*', { count: 'exact', head: true })
        .eq('manifest_id', manifest.id)
        .eq('is_deleted', false);
      const { count: sse } = await supabase
        .from('pob_entries')
        .select('*', { count: 'exact', head: true })
        .eq('manifest_id', manifest.id)
        .eq('is_deleted', false)
        .eq('is_sse', true);
      return { total: total ?? 0, sse: sse ?? 0 };
    },
  });

  const total = manifest.total_pob ?? sseQ.data?.total ?? 0;
  const sseCount = sseQ.data?.sse ?? 0;
  const ssePct = total > 0 ? Math.round((sseCount / total) * 100) : 0;

  return (
    <TableRow>
      <TableCell className="font-medium">
        {format(new Date(manifest.manifest_date + 'T00:00:00'), 'MMM d, yyyy')}
      </TableCell>
      <TableCell>{manifest.facility_name ?? '—'}</TableCell>
      <TableCell>{total}</TableCell>
      <TableCell>{sseCount}</TableCell>
      <TableCell>{ssePct}%</TableCell>
      <TableCell>
        <Badge className={`${STATUS_BADGE[manifest.status]} capitalize`}>
          {manifest.status}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          <Link
            to={`/pob/${manifest.id}`}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
            title="View"
          >
            <Eye className="h-4 w-4" />
          </Link>
          {canWrite && manifest.status !== 'archived' && (
            <>
              <Link
                to={`/pob/${manifest.id}`}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
                title="Edit"
              >
                <Pencil className="h-4 w-4" />
              </Link>
              <button
                onClick={() => onArchive(manifest.id)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
                title="Archive"
              >
                <Archive className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
